/* eslint-disable react/jsx-props-no-spreading */
import { AutocompleteWithLoading, TimeSelector } from "@iventis/components";
import { Schedule } from "@iventis/domain-model/model/schedule";
import { inputLeftPadding, inputMarginBottom, media, StyledFieldLabel, styled } from "@iventis/styles";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { TextField } from "@mui/material";
import { MobileDatePicker } from "@mui/x-date-pickers";
import React from "react";

/* eslint-disable @typescript-eslint/no-namespace */
declare namespace Intl {
    function supportedValuesOf(tz: string): string[];
}

export const ScheduleTimesComponent: React.FC<{ onChange: (schedule: Schedule) => void; schedule: Schedule }> = ({ onChange, schedule }) => {
    const translate = useIventisTranslate();

    const timezoneChanged = (newTz: string) => {
        onChange({ ...schedule, timezone: newTz });
    };

    const dayStartTimeChanged = (time: string) => {
        onChange({ ...schedule, dayStartAt: time });
    };

    const dayEndTimeChanged = (time: string) => {
        onChange({ ...schedule, dayEndAt: time });
    };

    const startDayChanged = (day: Date) => {
        onChange({ ...schedule, startAt: day });
    };

    const endDayChanged = (day: Date) => {
        onChange({ ...schedule, endAt: day });
    };

    const dayZeroChanged = (day: Date) => {
        onChange({ ...schedule, dayZero: day });
    };

    return (
        <StyledContainer>
            <StyledFieldLabel>{translate(Content.settings.scheduleConfig.timezone)}</StyledFieldLabel>
            <AutocompleteWithLoading
                className="input-margin-bottom"
                value={schedule?.timezone || ""}
                options={Intl.supportedValuesOf("timeZone")}
                loadingOptions={false}
                onChange={timezoneChanged}
                getOptionLabel={(opt) => opt}
            />
            <div className="split-3 input-margin-bottom">
                <div>
                    <StyledFieldLabel>{translate(Content.settings.scheduleConfig.projectStartDate)}</StyledFieldLabel>
                    <MobileDatePicker
                        className="fill-width"
                        inputFormat="dd/MM/yyyy"
                        mask="__/__/____"
                        value={schedule?.startAt || null}
                        onChange={(e) => startDayChanged(e)}
                        renderInput={(params) => <TextField {...params} />}
                    />
                </div>
                <div>
                    <StyledFieldLabel>{translate(Content.settings.scheduleConfig.projectZeroDate)}</StyledFieldLabel>
                    <MobileDatePicker
                        className="fill-width"
                        inputFormat="dd/MM/yyyy"
                        mask="__/__/____"
                        value={schedule?.dayZero || null}
                        onChange={(e) => dayZeroChanged(e)}
                        renderInput={(params) => <TextField {...params} />}
                    />
                </div>
                <div>
                    <StyledFieldLabel>{translate(Content.settings.scheduleConfig.projectEndDate)}</StyledFieldLabel>
                    <MobileDatePicker
                        className="fill-width"
                        inputFormat="dd/MM/yyyy"
                        mask="__/__/____"
                        value={schedule?.endAt || null}
                        onChange={(e) => endDayChanged(e)}
                        renderInput={(params) => <TextField {...params} />}
                    />
                </div>
            </div>
            <StyledFieldLabel>{translate(Content.settings.scheduleConfig.dayStartTime)}</StyledFieldLabel>
            <TimeSelector value={schedule?.dayStartAt || ""} onChange={(time) => dayStartTimeChanged(time.target.value)} />
            <StyledFieldLabel>{translate(Content.settings.scheduleConfig.dayEndTime)}</StyledFieldLabel>
            <TimeSelector value={schedule?.dayEndAt || ""} onChange={(time) => dayEndTimeChanged(time.target.value)} />
        </StyledContainer>
    );
};

const StyledContainer = styled.div`
    .split-3 {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: ${inputLeftPadding};

        ${media.extraSmall} {
            grid-template-columns: 1fr;
        }
    }

    .input-margin-bottom {
        margin-bottom: ${inputMarginBottom};
    }

    .fill-width {
        width: 100%;
    }
`;
